package dev.lingvo.site.api

import dev.lingvo.shared.DesignAssetDto
import dev.lingvo.shared.DesignAssetKind
import dev.lingvo.shared.DesignVersionSummaryDto
import dev.lingvo.shared.PutScreenshotDesignRequest
import dev.lingvo.shared.RestoreDesignRequest
import dev.lingvo.shared.ScreenshotDesignDto
import dev.lingvo.shared.StorePlatform
import kotlinx.serialization.decodeFromString
import org.w3c.files.Blob

/** Loads the platform's latest canonical design. */
suspend fun ApiClient.screenshotDesign(slug: String, platform: StorePlatform): ScreenshotDesignDto =
    jsonRequest("GET", designBase(slug, platform) + "/design")

/** Saves against an explicit base version so another editor's work cannot be overwritten. */
suspend fun ApiClient.putScreenshotDesign(
    slug: String, platform: StorePlatform, request: PutScreenshotDesignRequest,
): ScreenshotDesignDto = jsonRequest("PUT", designBase(slug, platform) + "/design", request)

/** Lists lightweight version summaries newest first. */
suspend fun ApiClient.screenshotDesignVersions(slug: String, platform: StorePlatform): List<DesignVersionSummaryDto> =
    jsonRequest("GET", designBase(slug, platform) + "/design/versions")

/** Loads an immutable historical version for inspection. */
suspend fun ApiClient.screenshotDesignVersion(slug: String, platform: StorePlatform, version: Int): ScreenshotDesignDto =
    jsonRequest("GET", designBase(slug, platform) + "/design/versions/${segment(version.toString())}")

/** Restoring creates a new version under optimistic concurrency. */
suspend fun ApiClient.restoreScreenshotDesign(
    slug: String, platform: StorePlatform, version: Int, request: RestoreDesignRequest,
): ScreenshotDesignDto = jsonRequest("POST", designBase(slug, platform) + "/design/versions/${segment(version.toString())}/restore", request)

/** Separates artwork metadata from authenticated image bytes. */
suspend fun ApiClient.screenshotDesignAssets(slug: String, platform: StorePlatform): List<DesignAssetDto> =
    jsonRequest("GET", designBase(slug, platform) + "/assets")

/** Client-chosen asset IDs remain path segments even when malformed input contains delimiters. */
fun ApiClient.screenshotDesignAssetUploadPath(slug: String, platform: StorePlatform, assetId: String, kind: DesignAssetKind): String =
    designBase(slug, platform) + "/assets/${segment(assetId)}" + query("kind" to kind.name)

/** Uploads artwork with progress and the same error decoding as other API requests. */
suspend fun ApiClient.putScreenshotDesignAsset(
    slug: String, platform: StorePlatform, assetId: String, kind: DesignAssetKind, body: Blob, contentType: String,
    upload: XhrUpload = XhrUpload(), onProgress: (Long, Long) -> Unit = { _, _ -> },
): DesignAssetDto = upload.put(screenshotDesignAssetUploadPath(slug, platform, assetId, kind), body, contentType,
    onProgress = onProgress) { API_JSON.decodeFromString<DesignAssetDto>(it) }

/** Fetches private artwork through the authenticated session transport. */
suspend fun ApiClient.screenshotDesignAssetImage(slug: String, platform: StorePlatform, assetId: String): Blob =
    blobRequest("GET", designBase(slug, platform) + "/assets/${segment(assetId)}/image", "image/png, image/jpeg")

/** Lets the server refuse deletion while a document still references the artwork. */
suspend fun ApiClient.deleteScreenshotDesignAsset(slug: String, platform: StorePlatform, assetId: String): Unit =
    unitRequest("DELETE", designBase(slug, platform) + "/assets/${segment(assetId)}")

private fun designBase(slug: String, platform: StorePlatform): String =
    "/projects/${segment(slug)}/screenshots/${segment(platform.id)}"
